/* ============================================================================
 * GFI Nexus — localStorage → Supabase one-shot migration.
 *
 *   import { exportLocalToSupabase } from './supabaseMigration';
 *   const summary = await exportLocalToSupabase((p) => console.log(p));
 *
 * What it does
 * ------------
 * Reads every `gfi-*` localStorage key, transforms each record into the
 * row shape defined by supabaseSchema.sql, and upserts in dependency
 * order (catalogs → operations → POs → config) so foreign keys resolve.
 *
 * Idempotency
 * -----------
 * Every table is upserted on the `legacy_id` column. Running the
 * migration twice doesn't duplicate rows — it updates the existing ones
 * with the latest localStorage shape. Safe to re-run.
 *
 * Attachments
 * -----------
 * Binary blobs (base64 PDFs, label photos, signatures) are stripped via
 * `stripBlobs()` — anything > 2 KB or beginning with `data:` is nulled
 * out. The migration is for STRUCTURED DATA ONLY. Files are a separate
 * Storage migration handled later.
 *
 * Cross-table refs
 * ----------------
 * Lookup ids in localStorage are text/number ("BILL-0042", a Date.now()
 * number). After each catalog table loads, we build
 * `idMaps[table][legacyId] = uuid` so the operations layer can resolve
 * `supplierId`, `clientId`, etc. into proper uuids alongside the
 * preserved `*_legacy_id` text columns.
 * ============================================================================
 */

import { supabase } from './supabaseClient';

/* ------------------------------- helpers ---------------------------------- */

function loadLS(key, fallback) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; }
  catch { return fallback; }
}

function asText(v) {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}
function asNumber(v) {
  if (v === undefined || v === null || v === '') return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}
function asInt(v) {
  if (v === undefined || v === null || v === '') return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
}
function asBool(v) {
  if (v === undefined || v === null || v === '') return null;
  return Boolean(v);
}
/* Date columns expect YYYY-MM-DD or null. localStorage stores ISO dates
   already, but some legacy rows have empty strings — coerce to null. */
function asDate(v) {
  const s = asText(v);
  if (!s) return null;
  // Accept "YYYY-MM-DD" or "YYYY-MM-DDTHH:..." — strip time if present.
  return s.length >= 10 ? s.slice(0, 10) : null;
}

/* Recursively null out anything that looks like an embedded blob so the
   JSONB column doesn't bloat. Preserves array length and object shape so
   downstream consumers see the same keys (just with null values where the
   blob used to live). The 2000-char threshold is generous — even long
   notes/descriptions stay; only base64 payloads get nulled. */
const BLOB_THRESHOLD = 2000;
function stripBlobs(value) {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    if (value.startsWith('data:')) return null;
    if (value.length > BLOB_THRESHOLD) return null;
    return value;
  }
  if (Array.isArray(value)) return value.map(stripBlobs);
  if (typeof value === 'object') {
    const out = {};
    for (const k of Object.keys(value)) out[k] = stripBlobs(value[k]);
    return out;
  }
  return value;
}

/* The catch-all `extra` column gets the full original record minus the
   keys we already promoted to typed columns. Lets a later schema
   evolution surface a forgotten field without re-running the migration. */
function extraOf(record, promotedKeys) {
  const out = {};
  for (const k of Object.keys(record || {})) {
    if (promotedKeys.indexOf(k) === -1) out[k] = stripBlobs(record[k]);
  }
  return out;
}

/* ----------------------------- id maps ------------------------------------ */
/* Populated by the upsert step of each catalog table; consumed by the
   operations transforms below to fill `<thing>_id` uuid columns. */
const idMaps = {
  seasons: {},
  suppliers: {},
  clients: {},
  products: {},
  warehouses: {},
  brokers: {},
  growers: {},
  certifications: {},
  export_certs: {},
  fee_catalog: {},
  shipments: {},
  shipper_shipments: {},
  bills: {},
  invoices: {},
  payments: {},
  expense_bills: {},
  fee_transfers: {},
  fixed_pos: {},
  consignment_pos: {},
};

function resolveFk(table, legacyId) {
  if (legacyId === undefined || legacyId === null || legacyId === '') return null;
  return idMaps[table][String(legacyId)] || null;
}

/* -------------------------- batch upsert core ----------------------------- */
const BATCH_SIZE = 250;

async function migrateTable({ table, lsKey, transform, onProgress, conflictKey }) {
  const onConflict = conflictKey || 'legacy_id';
  const raw = loadLS(lsKey, []);
  const records = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? Object.values(raw) : []);
  const rows = records
    .map((r, i) => {
      try { return transform(r, i); } catch (e) { return { __error: e.message, __record: r }; }
    })
    .filter((r) => r && !r.__error);
  const transformErrors = records.length - rows.length;

  onProgress({ table, lsKey, done: 0, total: rows.length, status: rows.length === 0 ? 'done' : 'running' });

  if (rows.length === 0) {
    return { table, lsKey, total: 0, migrated: 0, errors: [], transformErrors };
  }

  const errors = [];
  let migrated = 0;
  for (let i = 0; i < rows.length; i += BATCH_SIZE) {
    const slice = rows.slice(i, i + BATCH_SIZE);
    const { data, error } = await supabase
      .from(table)
      .upsert(slice, { onConflict })
      .select(conflictKey ? '*' : 'id, legacy_id');
    if (error) {
      errors.push({ batchStart: i, batchSize: slice.length, message: error.message, details: error.details || null });
    } else if (data && !conflictKey) {
      data.forEach((r) => { if (r.legacy_id) idMaps[table][r.legacy_id] = r.id; });
      migrated += data.length;
    } else if (data) {
      migrated += data.length;
    }
    onProgress({ table, lsKey, done: Math.min(i + BATCH_SIZE, rows.length), total: rows.length, status: 'running' });
  }
  onProgress({ table, lsKey, done: rows.length, total: rows.length, status: errors.length === 0 ? 'done' : 'partial' });
  return { table, lsKey, total: rows.length, migrated, errors, transformErrors };
}

/* ============================================================================
 * TRANSFORMS — one per table. Every transform must return at least
 * `legacy_id` (required by the upsert onConflict). Unknown / extra
 * fields are preserved in the `extra` jsonb column.
 * ============================================================================
 */

/* ---- 1. seasons ---- */
const SEASONS_COLS = ['id', 'seasonName', 'startDate', 'endEstimate', 'profitGoal', 'status', 'notes'];
function toSeasonRow(s) {
  return {
    legacy_id: asText(s.id),
    season_name: asText(s.seasonName) || asText(s.name) || 'Season',
    start_date: asDate(s.startDate),
    end_estimate: asDate(s.endEstimate),
    profit_goal: asNumber(s.profitGoal),
    status: asText(s.status) || 'open',
    notes: asText(s.notes),
    extra: extraOf(s, SEASONS_COLS),
  };
}

/* ---- 2. suppliers ---- */
const SUPPLIERS_COLS = ['id', 'supplierType', 'initials', 'shortName', 'fullName', 'contact', 'phone', 'email', 'address', 'colony', 'city', 'state', 'country', 'zip', 'paymentTerm', 'commissionRate', 'notes', 'term'];
function toSupplierRow(s) {
  return {
    legacy_id: asText(s.id),
    supplier_type: asText(s.supplierType) || 'shipper',
    initials: asText(s.initials),
    short_name: asText(s.shortName),
    full_name: asText(s.fullName),
    contact: asText(s.contact),
    phone: asText(s.phone),
    email: asText(s.email),
    address: asText(s.address),
    colony: asText(s.colony),
    city: asText(s.city),
    state: asText(s.state),
    country: asText(s.country),
    zip: asText(s.zip),
    payment_term: asText(s.paymentTerm) || asText(s.term),
    commission_rate: asNumber(s.commissionRate),
    notes: asText(s.notes),
    extra: extraOf(s, SUPPLIERS_COLS),
  };
}

/* ---- 3. clients ---- */
const CLIENTS_COLS = ['id', 'initials', 'shortName', 'fullName', 'company', 'dba', 'taxId', 'contact', 'phone', 'email', 'address', 'city', 'state', 'zip', 'country', 'paymentTerm', 'commissionRate', 'notes', 'term'];
function toClientRow(c) {
  return {
    legacy_id: asText(c.id),
    initials: asText(c.initials),
    short_name: asText(c.shortName),
    full_name: asText(c.fullName),
    company: asText(c.company),
    dba: asText(c.dba),
    tax_id: asText(c.taxId),
    contact: asText(c.contact),
    phone: asText(c.phone),
    email: asText(c.email),
    address: asText(c.address),
    city: asText(c.city),
    state: asText(c.state),
    zip: asText(c.zip),
    country: asText(c.country),
    payment_term: asText(c.paymentTerm) || asText(c.term),
    commission_rate: asNumber(c.commissionRate),
    notes: asText(c.notes),
    extra: extraOf(c, CLIENTS_COLS),
  };
}

/* ---- 4. products ---- */
const PRODUCTS_COLS = ['id', 'productCode', 'sku', 'product', 'packType', 'size', 'unitsPerCase', 'casesPerPallet', 'label', 'labelInitials', 'category', 'description', 'weightPerCaseLb', 'weightPerCaseKg', 'isActive', 'labelPhoto'];
function toProductRow(p) {
  return {
    legacy_id: asText(p.id),
    product_code: asText(p.productCode),
    sku: asText(p.sku),
    product: asText(p.product) || 'Unknown',
    pack_type: asText(p.packType),
    size: asText(p.size),
    units_per_case: asInt(p.unitsPerCase),
    cases_per_pallet: asInt(p.casesPerPallet),
    label: asText(p.label),
    label_initials: asText(p.labelInitials),
    category: asText(p.category),
    description: asText(p.description),
    weight_per_case_lb: asNumber(p.weightPerCaseLb),
    weight_per_case_kg: asNumber(p.weightPerCaseKg),
    is_active: p.isActive === false ? false : true,
    documents: [],
    extra: extraOf(p, PRODUCTS_COLS),
  };
}

/* ---- 5. warehouses ---- */
const WAREHOUSES_COLS = ['id', 'initials', 'shortName', 'fullName', 'address', 'city', 'state', 'zip', 'country', 'phone', 'email', 'notes'];
function toWarehouseRow(w) {
  return {
    legacy_id: asText(w.id),
    initials: asText(w.initials),
    short_name: asText(w.shortName),
    full_name: asText(w.fullName),
    address: asText(w.address),
    city: asText(w.city),
    state: asText(w.state),
    zip: asText(w.zip),
    country: asText(w.country),
    phone: asText(w.phone),
    email: asText(w.email),
    notes: asText(w.notes),
    extra: extraOf(w, WAREHOUSES_COLS),
  };
}

/* ---- 6. brokers ---- */
const BROKERS_COLS = ['id', 'shortName', 'fullName', 'initials', 'contact', 'phone', 'email', 'addresses', 'notes', 'mxAddress', 'usAddress'];
function toBrokerRow(b) {
  const addresses = b.addresses || (b.mxAddress || b.usAddress ? { mx: b.mxAddress || null, us: b.usAddress || null } : {});
  return {
    legacy_id: asText(b.id),
    short_name: asText(b.shortName),
    full_name: asText(b.fullName),
    initials: asText(b.initials),
    contact: asText(b.contact),
    phone: asText(b.phone),
    email: asText(b.email),
    addresses: stripBlobs(addresses),
    notes: asText(b.notes),
    extra: extraOf(b, BROKERS_COLS),
  };
}

/* ---- 7. growers ---- */
const GROWERS_COLS = ['id', 'supplierId', 'shortName', 'fullName', 'contact', 'phone', 'email', 'location', 'productionType', 'certifications', 'notes'];
function toGrowerRow(g) {
  return {
    legacy_id: asText(g.id),
    supplier_legacy_id: asText(g.supplierId),
    supplier_id: resolveFk('suppliers', g.supplierId),
    short_name: asText(g.shortName),
    full_name: asText(g.fullName),
    contact: asText(g.contact),
    phone: asText(g.phone),
    email: asText(g.email),
    location: asText(g.location),
    production_type: asText(g.productionType),
    certifications: stripBlobs(g.certifications || []),
    notes: asText(g.notes),
    extra: extraOf(g, GROWERS_COLS),
  };
}

/* ---- 8. certifications ---- */
const CERTIFICATIONS_COLS = ['id', 'certNumber', 'certType', 'certifier', 'issuedDate', 'expiresDate', 'status', 'document', 'notes', 'pdf', 'documentData'];
function toCertificationRow(c) {
  return {
    legacy_id: asText(c.id),
    cert_number: asText(c.certNumber) || 'UNKNOWN',
    cert_type: asText(c.certType),
    certifier: asText(c.certifier),
    issued_date: asDate(c.issuedDate),
    expires_date: asDate(c.expiresDate),
    status: asText(c.status) || 'active',
    /* Document metadata only; binary lives in Supabase Storage. */
    document: stripBlobs({ name: c.documentName || null, size: c.documentSize || null, type: c.documentType || null }),
    notes: asText(c.notes),
    extra: extraOf(c, CERTIFICATIONS_COLS),
  };
}

/* ---- 9. export_certs ---- */
const EXPORT_CERTS_COLS = ['id', 'certNumber', 'supplierId', 'shipperName', 'productName', 'product', 'certifier', 'periodFrom', 'periodTo', 'totalInvoices', 'usedInvoices', 'linkedLots', 'document', 'notes', 'pdf', 'documentData'];
function toExportCertRow(c) {
  return {
    legacy_id: asText(c.id),
    cert_number: asText(c.certNumber) || 'UNKNOWN',
    supplier_legacy_id: asText(c.supplierId),
    supplier_id: resolveFk('suppliers', c.supplierId),
    shipper_name: asText(c.shipperName),
    product_name: asText(c.productName) || asText(c.product),
    certifier: asText(c.certifier),
    period_from: asDate(c.periodFrom),
    period_to: asDate(c.periodTo),
    total_invoices: asInt(c.totalInvoices),
    used_invoices: stripBlobs(c.usedInvoices || []),
    linked_lots: stripBlobs(c.linkedLots || []),
    document: stripBlobs({ name: c.documentName || null, size: c.documentSize || null, type: c.documentType || null }),
    notes: asText(c.notes),
    extra: extraOf(c, EXPORT_CERTS_COLS),
  };
}

/* ---- 10. fee_catalog ---- */
function toFeeCatalogRow(f) {
  /* gfi-fee-catalog can be stored either as an array of {name, ...} or
     as a simple array of strings (legacy "fee types" list). Normalize. */
  const obj = typeof f === 'string' ? { name: f } : (f || {});
  return {
    legacy_id: asText(obj.id) || asText(obj.name),
    name: asText(obj.name) || 'Unnamed',
    default_rate: asNumber(obj.defaultRate),
    default_qty: asNumber(obj.defaultQty),
    default_passthrough: obj.defaultPassthrough === true,
    is_customs_fee: obj.isCustomsFee === true,
    notes: asText(obj.notes),
    extra: extraOf(obj, ['id', 'name', 'defaultRate', 'defaultQty', 'defaultPassthrough', 'isCustomsFee', 'notes']),
  };
}

/* ---- 11. shipments ---- */
const SHIPMENTS_COLS = ['id', 'lot', 'seasonId', 'supplierId', 'shipperName', 'shipperSource', 'dateDeparture', 'dateArrival', 'week', 'proforma', 'shipperInvoice', 'entryNumber', 'alphaCode', 'numEconomico', 'codigoAlpha', 'warehouseId', 'warehouseName', 'brokerId', 'agenteAduanal', 'status', 'lines', 'palletManifest', 'transportData', 'documents', 'notes'];
function toShipmentRow(s) {
  return {
    legacy_id: asText(s.id),
    lot: asText(s.lot),
    season_legacy_id: asText(s.seasonId),
    season_id: resolveFk('seasons', s.seasonId),
    supplier_legacy_id: asText(s.supplierId),
    supplier_id: resolveFk('suppliers', s.supplierId),
    shipper_name: asText(s.shipperName),
    shipper_source: asText(s.shipperSource),
    date_departure: asDate(s.dateDeparture),
    date_arrival: asDate(s.dateArrival),
    week: asText(s.week),
    proforma: asText(s.proforma),
    shipper_invoice: asText(s.shipperInvoice),
    entry_number: asText(s.entryNumber),
    alpha_code: asText(s.alphaCode) || asText(s.codigoAlpha),
    num_economico: asText(s.numEconomico),
    warehouse_legacy_id: asText(s.warehouseId),
    warehouse_id: resolveFk('warehouses', s.warehouseId),
    warehouse_name: asText(s.warehouseName),
    broker_legacy_id: asText(s.brokerId),
    broker_id: resolveFk('brokers', s.brokerId),
    status: asText(s.status),
    lines: stripBlobs(s.lines || []),
    pallet_manifest: stripBlobs(s.palletManifest || []),
    transport_data: stripBlobs(s.transportData || {}),
    documents: stripBlobs(s.documents || []),
    notes: asText(s.notes),
    extra: extraOf(s, SHIPMENTS_COLS),
  };
}

/* ---- 12. shipper_shipments (portal) ---- */
const SHIPPER_SHIPMENTS_COLS = ['id', 'supplierId', 'consecEmbarque', 'shipperInvoice', 'proformas', 'docs', 'notes'];
function toShipperShipmentRow(s) {
  return {
    legacy_id: asText(s.id),
    supplier_legacy_id: asText(s.supplierId),
    supplier_id: resolveFk('suppliers', s.supplierId),
    consec_embarque: asText(s.consecEmbarque),
    shipper_invoice: asText(s.shipperInvoice),
    proformas: stripBlobs(s.proformas || []),
    docs: stripBlobs(s.docs || {}),
    notes: asText(s.notes),
    extra: extraOf(s, SHIPPER_SHIPMENTS_COLS),
  };
}

/* ---- 13. bills ---- */
const BILLS_COLS = ['id', 'lot', 'shipmentId', 'seasonId', 'supplierId', 'shipperName', 'billDate', 'dueDate', 'proforma', 'shipperInvoice', 'entryNumber', 'reference', 'commissionRate', 'status', 'amountPaid', 'lessAmountPaid', 'amountDue', 'lines', 'fees', 'credits', 'balanceTransfers', 'payments', 'exportCerts', 'documents', 'notes', 'linkedInvoices', 'linkedInvoiceObjs'];
function toBillRow(b) {
  return {
    legacy_id: asText(b.id),
    lot: asText(b.lot),
    shipment_legacy_id: asText(b.shipmentId),
    shipment_id: resolveFk('shipments', b.shipmentId),
    season_legacy_id: asText(b.seasonId),
    season_id: resolveFk('seasons', b.seasonId),
    supplier_legacy_id: asText(b.supplierId),
    supplier_id: resolveFk('suppliers', b.supplierId),
    shipper_name: asText(b.shipperName),
    bill_date: asDate(b.billDate),
    due_date: asDate(b.dueDate),
    proforma: asText(b.proforma),
    shipper_invoice: asText(b.shipperInvoice),
    entry_number: asText(b.entryNumber),
    reference: asText(b.reference),
    commission_rate: asNumber(b.commissionRate),
    status: asText(b.status) || 'draft',
    amount_paid: asNumber(b.amountPaid) ?? asNumber(b.lessAmountPaid),
    amount_due: asNumber(b.amountDue),
    lines: stripBlobs(b.lines || []),
    fees: stripBlobs(b.fees || []),
    credits: stripBlobs(b.credits || []),
    balance_transfers: stripBlobs(b.balanceTransfers || []),
    payments: stripBlobs(b.payments || []),
    export_certs: stripBlobs(b.exportCerts || []),
    documents: stripBlobs(b.documents || []),
    notes: asText(b.notes),
    extra: extraOf(b, BILLS_COLS),
  };
}

/* ---- 14. invoices ---- */
const INVOICES_COLS = ['id', 'puNumber', 'date', 'dueDate', 'seasonId', 'clientId', 'clientName', 'clientInitials', 'clientRef', 'lot', 'shipmentId', 'billId', 'shipperName', 'commissionRate', 'status', 'amountPaid', 'lines', 'fees', 'credits', 'balanceTransfers', 'payments', 'linkedLots', 'linkedPoLineId', 'remarks', 'documents'];
function toInvoiceRow(i) {
  return {
    legacy_id: asText(i.id),
    pu_number: asText(i.puNumber),
    invoice_date: asDate(i.date),
    due_date: asDate(i.dueDate),
    season_legacy_id: asText(i.seasonId),
    season_id: resolveFk('seasons', i.seasonId),
    client_legacy_id: asText(i.clientId),
    client_id: resolveFk('clients', i.clientId),
    client_name: asText(i.clientName),
    client_initials: asText(i.clientInitials),
    client_ref: asText(i.clientRef),
    lot: asText(i.lot),
    shipment_legacy_id: asText(i.shipmentId),
    shipment_id: resolveFk('shipments', i.shipmentId),
    bill_legacy_id: asText(i.billId),
    bill_id: resolveFk('bills', i.billId),
    shipper_name: asText(i.shipperName),
    commission_rate: asNumber(i.commissionRate),
    status: asText(i.status) || 'draft',
    amount_paid: asNumber(i.amountPaid),
    lines: stripBlobs(i.lines || []),
    fees: stripBlobs(i.fees || []),
    credits: stripBlobs(i.credits || []),
    balance_transfers: stripBlobs(i.balanceTransfers || []),
    payments: stripBlobs(i.payments || []),
    linked_lots: stripBlobs(i.linkedLots || []),
    linked_po_line_id: asText(i.linkedPoLineId),
    remarks: asText(i.remarks),
    documents: stripBlobs(i.documents || []),
    extra: extraOf(i, INVOICES_COLS),
  };
}

/* ---- 15. payments ---- */
const PAYMENTS_COLS = ['id', 'type', 'paymentDate', 'amount', 'paymentMethod', 'reference', 'clientName', 'shipperName', 'clientId', 'supplierId', 'allocations', 'notes'];
function toPaymentRow(p) {
  return {
    legacy_id: asText(p.id),
    payment_type: asText(p.type) || 'inbound',
    payment_date: asDate(p.paymentDate),
    amount: asNumber(p.amount),
    payment_method: asText(p.paymentMethod),
    reference: asText(p.reference),
    client_name: asText(p.clientName),
    shipper_name: asText(p.shipperName),
    client_legacy_id: asText(p.clientId),
    client_id: resolveFk('clients', p.clientId),
    supplier_legacy_id: asText(p.supplierId),
    supplier_id: resolveFk('suppliers', p.supplierId),
    allocations: stripBlobs(p.allocations || []),
    notes: asText(p.notes),
    extra: extraOf(p, PAYMENTS_COLS),
  };
}

/* ---- 16. expense_bills ---- */
const EXPENSE_BILLS_COLS = ['id', 'vendor', 'vendorId', 'reference', 'date', 'dueDate', 'amount', 'amountPaid', 'status', 'paymentMethod', 'paymentReference', 'fees', 'distribution', 'documents', 'notes'];
function toExpenseBillRow(e) {
  return {
    legacy_id: asText(e.id),
    vendor: asText(e.vendor) || 'Unknown',
    vendor_legacy_id: asText(e.vendorId),
    vendor_id: resolveFk('suppliers', e.vendorId),
    reference: asText(e.reference),
    expense_date: asDate(e.date),
    due_date: asDate(e.dueDate),
    amount: asNumber(e.amount),
    amount_paid: asNumber(e.amountPaid),
    status: asText(e.status) || 'open',
    payment_method: asText(e.paymentMethod),
    payment_reference: asText(e.paymentReference),
    fees: stripBlobs(e.fees || []),
    distribution: stripBlobs(e.distribution || []),
    documents: stripBlobs(e.documents || []),
    notes: asText(e.notes),
    extra: extraOf(e, EXPENSE_BILLS_COLS),
  };
}

/* ---- 17. fee_transfers ---- */
const FEE_TRANSFERS_COLS = ['id', 'sourceType', 'sourceInvoice', 'sourceBill', 'sourcePU', 'targetType', 'targetLot', 'targetBillId', 'targetInvoiceId', 'feeType', 'feeCustom', 'qty', 'rate', 'amount', 'pctRate', 'status', 'dateCreated', 'notes'];
function toFeeTransferRow(t) {
  return {
    legacy_id: asText(t.id),
    source_type: asText(t.sourceType) || 'invoice',
    source_invoice_legacy_id: asText(t.sourceInvoice),
    source_invoice_id: resolveFk('invoices', t.sourceInvoice),
    source_bill_legacy_id: asText(t.sourceBill),
    source_bill_id: resolveFk('bills', t.sourceBill),
    source_pu: asText(t.sourcePU),
    target_type: asText(t.targetType) || 'bill',
    target_lot: asText(t.targetLot),
    target_bill_legacy_id: asText(t.targetBillId),
    target_bill_id: resolveFk('bills', t.targetBillId),
    target_invoice_legacy_id: asText(t.targetInvoiceId),
    target_invoice_id: resolveFk('invoices', t.targetInvoiceId),
    fee_type: asText(t.feeType),
    fee_custom: asText(t.feeCustom),
    qty: asNumber(t.qty),
    rate: asNumber(t.rate),
    amount: asNumber(t.amount),
    pct_rate: asText(t.pctRate),
    status: asText(t.status) || 'pending',
    date_created: asDate(t.dateCreated) || new Date().toISOString().slice(0, 10),
    notes: asText(t.notes),
    extra: extraOf(t, FEE_TRANSFERS_COLS),
  };
}

/* ---- 18. fixed_pos ---- */
const FIXED_POS_COLS = ['id', 'poNumber', 'puNumber', 'date', 'week', 'deliveryDate', 'shipDate', 'clientId', 'clientName', 'productId', 'productBase', 'productName', 'productDescription', 'category', 'palletsSent', 'casesSent', 'loggerNumber', 'salePrice', 'reportedPrice', 'commissionRate', 'saleStatus', 'linkedInvoiceId', 'notes'];
function toFixedPoRow(p) {
  return {
    legacy_id: asText(p.id),
    po_number: asText(p.poNumber),
    pu_number: asText(p.puNumber),
    po_date: asDate(p.date),
    week: asText(p.week),
    delivery_date: asDate(p.deliveryDate),
    ship_date: asDate(p.shipDate),
    client_legacy_id: asText(p.clientId),
    client_id: resolveFk('clients', p.clientId),
    client_name: asText(p.clientName),
    product_legacy_id: asText(p.productId),
    product_id: resolveFk('products', p.productId),
    product_base: asText(p.productBase),
    product_name: asText(p.productName),
    product_description: asText(p.productDescription),
    category: asText(p.category),
    pallets_sent: asNumber(p.palletsSent),
    cases_sent: asNumber(p.casesSent),
    logger_number: asText(p.loggerNumber),
    sale_price: asNumber(p.salePrice),
    reported_price: asNumber(p.reportedPrice),
    commission_rate: asNumber(p.commissionRate),
    sale_status: asText(p.saleStatus),
    linked_invoice_legacy_id: asText(p.linkedInvoiceId),
    linked_invoice_id: resolveFk('invoices', p.linkedInvoiceId),
    notes: asText(p.notes),
    extra: extraOf(p, FIXED_POS_COLS),
  };
}

/* ---- 19. consignment_pos ---- */
const CONSIGN_POS_COLS = ['id', 'poNumber', 'puNumber', 'date', 'week', 'deliveryDate', 'shipDate', 'clientId', 'clientName', 'productId', 'productBase', 'productName', 'productDescription', 'category', 'palletsSent', 'casesSent', 'loggerNumber', 'priceLow', 'priceHigh', 'priceMidpoint', 'salePrice', 'reportedLow', 'reportedHigh', 'reportedPrice', 'clientCommissionRate', 'gfiCommissionRate', 'saleStatus', 'linkedInvoiceId', 'notes'];
function toConsignmentPoRow(p) {
  return {
    legacy_id: asText(p.id),
    po_number: asText(p.poNumber),
    pu_number: asText(p.puNumber),
    po_date: asDate(p.date),
    week: asText(p.week),
    delivery_date: asDate(p.deliveryDate),
    ship_date: asDate(p.shipDate),
    client_legacy_id: asText(p.clientId),
    client_id: resolveFk('clients', p.clientId),
    client_name: asText(p.clientName),
    product_legacy_id: asText(p.productId),
    product_id: resolveFk('products', p.productId),
    product_base: asText(p.productBase),
    product_name: asText(p.productName),
    product_description: asText(p.productDescription),
    category: asText(p.category),
    pallets_sent: asNumber(p.palletsSent),
    cases_sent: asNumber(p.casesSent),
    logger_number: asText(p.loggerNumber),
    price_low: asNumber(p.priceLow),
    price_high: asNumber(p.priceHigh),
    price_midpoint: asNumber(p.priceMidpoint),
    sale_price: asNumber(p.salePrice),
    reported_low: asNumber(p.reportedLow),
    reported_high: asNumber(p.reportedHigh),
    reported_price: asNumber(p.reportedPrice),
    client_commission_rate: asNumber(p.clientCommissionRate),
    gfi_commission_rate: asNumber(p.gfiCommissionRate),
    sale_status: asText(p.saleStatus),
    linked_invoice_legacy_id: asText(p.linkedInvoiceId),
    linked_invoice_id: resolveFk('invoices', p.linkedInvoiceId),
    notes: asText(p.notes),
    extra: extraOf(p, CONSIGN_POS_COLS),
  };
}

/* ---- 20. shipper_settings ---- */
/* gfi-shipper-settings is an object keyed by supplierId. Flatten into
   one row per supplier. We use supplier_id as the PK (composite-style),
   so conflictKey = 'supplier_id' instead of legacy_id. */
function expandShipperSettings() {
  const map = loadLS('gfi-shipper-settings', {}) || {};
  const out = [];
  for (const supplierLegacyId of Object.keys(map)) {
    const rec = map[supplierLegacyId] || {};
    const supplierId = resolveFk('suppliers', supplierLegacyId);
    if (!supplierId) continue; // can't insert without a real supplier uuid
    out.push({
      supplier_id: supplierId,
      supplier_legacy_id: String(supplierLegacyId),
      invoice_prefix: asText(rec.invoicePrefix),
      proforma_prefix: asText(rec.proformaPrefix),
      next_proforma_number: asInt(rec.nextProformaNumber) ?? 1,
      embarque_prefix: asText(rec.embarquePrefix),
      next_embarque_number: asInt(rec.nextEmbarqueNumber) ?? 1,
      authorized_products: stripBlobs(rec.authorizedProducts || []),
      assigned_brokers: stripBlobs(rec.assignedBrokers || []),
      default_us_broker_id: resolveFk('brokers', rec.defaultUSBroker),
      extra: extraOf(rec, ['invoicePrefix', 'proformaPrefix', 'nextProformaNumber', 'embarquePrefix', 'nextEmbarqueNumber', 'authorizedProducts', 'assignedBrokers', 'defaultUSBroker']),
    });
  }
  return out;
}

/* ---- 21. company_profile ---- */
/* Singleton — reads gfi-shipper-consignee (the Arturo-edited "shipper
   sees this as consignee" record) and/or gfi-company-profile. */
function toCompanyProfileRow() {
  const consignee = loadLS('gfi-shipper-consignee', {}) || {};
  const profile = loadLS('gfi-company-profile', {}) || {};
  const merged = { ...consignee, ...profile };
  if (!merged.companyName && !consignee.companyName && !profile.companyName) {
    /* Nothing to migrate. */
    return null;
  }
  return {
    id: 'gfi',
    company_name: asText(merged.companyName) || 'Ground Fresh International Inc.',
    address_line: asText(merged.addressLine),
    city: asText(merged.city),
    state: asText(merged.state),
    zip: asText(merged.zip),
    country: asText(merged.country),
    phone: asText(merged.phone),
    email: asText(merged.email),
    website: asText(merged.website),
    organic_cert_number: asText(merged.organicCertNumber),
    organic_certifier: asText(merged.organicCertifier),
    branding: stripBlobs(merged.branding || {}),
    notes: asText(merged.notes),
    extra: extraOf(merged, ['companyName', 'addressLine', 'city', 'state', 'zip', 'country', 'phone', 'email', 'website', 'organicCertNumber', 'organicCertifier', 'branding', 'notes']),
  };
}

/* ============================================================================
 * EXPORT — the one-shot driver. Migrates every table in FK-safe order,
 * reporting progress on each step via the onProgress callback.
 * ============================================================================
 */

/* The ordered task list. `pkConflict` is set for tables whose unique key
   is not `legacy_id` (e.g. shipper_settings uses supplier_id). */
export const MIGRATION_TASKS = [
  // ---- Catalogs (no FKs to operations) ----
  { table: 'seasons',         lsKey: 'gfi-seasons',          transform: toSeasonRow },
  { table: 'suppliers',       lsKey: 'gfi-suppliers',        transform: toSupplierRow },
  { table: 'clients',         lsKey: 'gfi-clients',          transform: toClientRow },
  { table: 'products',        lsKey: 'gfi-products',         transform: toProductRow },
  { table: 'warehouses',      lsKey: 'gfi-warehouses',       transform: toWarehouseRow },
  { table: 'brokers',         lsKey: 'gfi-brokers',          transform: toBrokerRow,         altKeys: ['gfi-customs-brokers'] },
  { table: 'growers',         lsKey: 'gfi-growers',          transform: toGrowerRow },
  { table: 'certifications',  lsKey: 'gfi-certifications',   transform: toCertificationRow },
  { table: 'export_certs',    lsKey: 'gfi-export-certs',     transform: toExportCertRow },
  { table: 'fee_catalog',     lsKey: 'gfi-fee-catalog',      transform: toFeeCatalogRow,     altKeys: ['gfi-fee-types', 'gfi-custom-fee-types'] },
  // ---- Operations (depend on catalogs being in idMaps) ----
  { table: 'shipments',         lsKey: 'gfi-shipments',         transform: toShipmentRow },
  { table: 'shipper_shipments', lsKey: 'gfi-shipper-shipments', transform: toShipperShipmentRow },
  { table: 'bills',             lsKey: 'gfi-bills',             transform: toBillRow },
  { table: 'invoices',          lsKey: 'gfi-invoices',          transform: toInvoiceRow },
  { table: 'payments',          lsKey: 'gfi-payments',          transform: toPaymentRow },
  { table: 'expense_bills',     lsKey: 'gfi-expense-bills',     transform: toExpenseBillRow },
  { table: 'fee_transfers',     lsKey: 'gfi-fee-transfers',     transform: toFeeTransferRow },
  // ---- POs ----
  { table: 'fixed_pos',         lsKey: 'gfi-fixed-pos',         transform: toFixedPoRow },
  { table: 'consignment_pos',   lsKey: 'gfi-consignment-pos',   transform: toConsignmentPoRow },
];

/* Some legacy installs use alternate keys. If the primary key has nothing
   but an alt key has data, swap the lsKey before running. */
function resolveAltKey(task) {
  const raw = loadLS(task.lsKey, []);
  if (Array.isArray(raw) && raw.length > 0) return task;
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && Object.keys(raw).length > 0) return task;
  if (task.altKeys) {
    for (const alt of task.altKeys) {
      const altRaw = loadLS(alt, []);
      if (Array.isArray(altRaw) && altRaw.length > 0) return { ...task, lsKey: alt };
      if (altRaw && typeof altRaw === 'object' && !Array.isArray(altRaw) && Object.keys(altRaw).length > 0) return { ...task, lsKey: alt };
    }
  }
  return task;
}

export async function exportLocalToSupabase(onProgress) {
  const cb = typeof onProgress === 'function' ? onProgress : () => {};
  const summary = { startedAt: new Date().toISOString(), tables: [] };

  /* Reset id maps so re-running the function gives the same result. */
  for (const k of Object.keys(idMaps)) idMaps[k] = {};

  for (const rawTask of MIGRATION_TASKS) {
    const task = resolveAltKey(rawTask);
    const result = await migrateTable({
      table: task.table,
      lsKey: task.lsKey,
      transform: task.transform,
      onProgress: cb,
    });
    summary.tables.push(result);
  }

  /* shipper_settings — needs suppliers + brokers idMaps, so runs after
     both. Object-keyed in localStorage; expand inline and upsert via
     supplier_id PK conflict. */
  {
    const rows = expandShipperSettings();
    cb({ table: 'shipper_settings', lsKey: 'gfi-shipper-settings', done: 0, total: rows.length, status: rows.length === 0 ? 'done' : 'running' });
    let migrated = 0;
    const errors = [];
    if (rows.length > 0) {
      for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        const slice = rows.slice(i, i + BATCH_SIZE);
        const { data, error } = await supabase
          .from('shipper_settings')
          .upsert(slice, { onConflict: 'supplier_id' })
          .select('supplier_id');
        if (error) errors.push({ batchStart: i, message: error.message, details: error.details || null });
        else if (data) migrated += data.length;
        cb({ table: 'shipper_settings', lsKey: 'gfi-shipper-settings', done: Math.min(i + BATCH_SIZE, rows.length), total: rows.length, status: 'running' });
      }
    }
    cb({ table: 'shipper_settings', lsKey: 'gfi-shipper-settings', done: rows.length, total: rows.length, status: errors.length === 0 ? 'done' : 'partial' });
    summary.tables.push({ table: 'shipper_settings', lsKey: 'gfi-shipper-settings', total: rows.length, migrated, errors });
  }

  /* company_profile — singleton, PK 'gfi'. */
  {
    const row = toCompanyProfileRow();
    cb({ table: 'company_profile', lsKey: 'gfi-company-profile', done: 0, total: row ? 1 : 0, status: row ? 'running' : 'done' });
    const errors = [];
    let migrated = 0;
    if (row) {
      const { data, error } = await supabase
        .from('company_profile')
        .upsert([row], { onConflict: 'id' })
        .select('id');
      if (error) errors.push({ message: error.message, details: error.details || null });
      else if (data) migrated = data.length;
    }
    cb({ table: 'company_profile', lsKey: 'gfi-company-profile', done: row ? 1 : 0, total: row ? 1 : 0, status: errors.length === 0 ? 'done' : 'partial' });
    summary.tables.push({ table: 'company_profile', lsKey: 'gfi-company-profile', total: row ? 1 : 0, migrated, errors });
  }

  summary.endedAt = new Date().toISOString();
  summary.totalMigrated = summary.tables.reduce((s, t) => s + (t.migrated || 0), 0);
  summary.totalErrors = summary.tables.reduce((s, t) => s + (t.errors ? t.errors.length : 0), 0);
  return summary;
}

/* ── One-shot repair: invoice.clientId from clientName ──
   Migrated invoices sometimes carry a partial / abbreviated
   clientName (e.g. "Four Seasons") and no clientId. This walks every
   invoice and matches it against the Clients catalog by substring on
   shortName / fullName / dba. Matches stamp { clientId, clientName }
   so downstream consumers (email, prints, settlements) all resolve
   the same canonical client. Returns { totalScanned, fixed, skipped }
   and writes the fixed rows back via saveManyToSupabase. */
export async function fixInvoiceClientIds() {
  const { loadFromSupabase, saveManyToSupabase } = await import('./supabaseDB.js');
  const [invoices, clients] = await Promise.all([
    loadFromSupabase('invoices', { force: true }),
    loadFromSupabase('clients', { force: true }),
  ]);
  const norm = (s) => String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, ' ').trim();
  const clientByLegacyId = new Map(clients.map(c => [String(c.id), c]));
  /* Build matcher payloads — at least 4 chars to avoid spurious
     2-letter matches like "FS" colliding with anything. */
  const matchers = clients.map(c => ({
    client: c,
    needles: [c.shortName, c.fullName, c.dba]
      .filter(Boolean)
      .map(norm)
      .filter(s => s.length >= 4),
  }));
  const fixed = [];
  let skipped = 0;
  for (const inv of invoices) {
    if (inv.clientId && clientByLegacyId.has(String(inv.clientId))) {
      /* clientId already resolves — skip, but normalize clientName
         to the catalog's canonical short name so the display stays
         consistent. */
      const c = clientByLegacyId.get(String(inv.clientId));
      const canonical = c.shortName || c.fullName || c.name || inv.clientName;
      if (canonical && inv.clientName !== canonical) {
        fixed.push({ ...inv, clientName: canonical });
      } else {
        skipped++;
      }
      continue;
    }
    const target = norm(inv.clientName || '');
    if (!target) { skipped++; continue; }
    let bestMatch = null;
    for (const m of matchers) {
      for (const needle of m.needles) {
        /* Either direction matches: invoice name contains client
           short ("Four Seasons" → matches "Four Seasons Produce")
           OR client short contains invoice name (partial migration
           leftovers). */
        if (target.indexOf(needle) >= 0 || needle.indexOf(target) >= 0) {
          bestMatch = m.client;
          break;
        }
      }
      if (bestMatch) break;
    }
    if (bestMatch) {
      fixed.push({
        ...inv,
        clientId: bestMatch.id,
        clientName: bestMatch.shortName || bestMatch.fullName || inv.clientName,
      });
    } else {
      skipped++;
    }
  }
  if (fixed.length > 0) {
    await saveManyToSupabase('invoices', fixed);
  }
  return { totalScanned: invoices.length, fixed: fixed.length, skipped: skipped };
}
